import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FaDownload } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import './Lot.css';
import Tabs  from '../lot-tracking/tabs';
const LotDetails = () => {
  const { id } = useParams();
  
    const [lotData, setLotData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
  
    useEffect(() => {
      const fetchData = async () => {
        try {
          const response = await fetch(`https://backend.hira-tech.co.uk/inventory/api/manufacturing/${id}/`);
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          const data = await response.json();
          setLotData(data);
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
      };
    
      fetchData();
    }, [id]);
  
    if (loading) return <div className="loading">Loading...</div>;
    if (error) return <div className="error">Error: {error}</div>;
    const handleDownloadPDF = () => {
      const doc = new jsPDF();
      const logo = "/logo.ico";
      doc.addImage(logo, "PNG", 140, 2, 60, 30);
      doc.setFontSize(16);
      doc.text("Manufacturing Lot Record", 14, 20);
      
    
      const basicInfo = [
        ["Manufacture Date", lotData.manufacturing_date],
        ["Lot Number", lotData.lot_number],
        ["Part Name", lotData.part_name],
        ["Part Type", lotData.part_type],
        ["Part Reference Number", lotData.part_reference_number],
      ];
    
      const machineUsers = [
        ["Machine Operator", lotData.machine_operator],
        ["Machine Inspection", lotData.machine_inspection],
      ];
    
      const weightDetails = [
        ["Part Per Weight", lotData.part_per_weight],
        ["Total Part Weight", lotData.total_part_weight],
        ["Quantity", lotData.quantity],
      ];
    
      const machineInfo = [
        ["Machine Number", lotData.machine_number],
        ["Material Lot Number", lotData.material_lot_number],
        ["Internal Lot Number", lotData.internal_lot_number],
      ];
    
      const dimensions = [
        ["Part Length (mm)", lotData.part_length_mm],
        ["Part Length incl. waste (1.4mm)", lotData.part_length_mm_include_waste],
        ["Manuf. with waste length (mm)", lotData.part_manufacturing_with_wastage_length_mm],
        ["Manuf. with waste length (No's)", lotData.part_manufacturing_with_wastage_length_nos],
      ];
    
      const barDetails = [
        ["Issue bar for manufacturing", lotData.issue_bar_for_manufacturing],
        ["Return balance bars (No's)", lotData.return_balance_bar_in_nos],
        ["Return balance bar (mm)", lotData.return_balance_bar_in_mm],
        ["Per length mm wastage (150mm)", lotData.per_length_mm_wastage_150mm],
        ["Wastage (150mm)", lotData.wastage_150mm],
        ["Final used bar (No's)", lotData.final_used_bar_in_nos],
        ["Final used bar (MM)", lotData.final_used_bar_in_mm],
        ["Each part waste", lotData.each_part_waste],
      ];
    
      const sections = [
        { title: "Basic Information", data: basicInfo },
        { title: "Machine Users", data: machineUsers },
        { title: "Weight Details", data: weightDetails },
        { title: "Machine Information", data: machineInfo },
        { title: "Dimensions (mm)", data: dimensions },
        { title: "Bar Details", data: barDetails },
      ];
    
      let startY = 30;
    
      sections.forEach((section) => {
        doc.setFontSize(14);
        doc.text(section.title, 14, startY);
        startY += 2;
        autoTable(doc, {
          startY: startY + 5,
          head: [["Field", "Value"]],
          body: section.data,
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: {
            fillColor: [0, 74, 173], // RGB for #004aad
            textColor: [255, 255, 255], // white text
            fontStyle: 'bold',
          },
        });
        startY = doc.lastAutoTable.finalY + 10;
      });
    
      doc.save(`Lot_${lotData.lot_number || id}.pdf`);
    };
    
    return (
      <div className="lot-container">
        {/* Header Section */}
        <div className="lot-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
  <h1 style={{ fontSize: "24px", fontWeight: "bold" }}>Manufacturing Lot Record</h1>
  <button
    onClick={handleDownloadPDF}
    style={{
      backgroundColor: "#004aad",
      color: "white",
      padding: "8px 14px",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "14px"
    }}
  >
    <FaDownload /> Download PDF
  </button>
</div>
        </div>
  
        {/* Main Content */}
        <div className="lot-content">
          {/* Left Column */}
          <div className="lot-column">
            {/* Basic Information */}
            <div className="lot-section">
              <h2>Basic Information</h2>
              <div className="lot-field">
                <span>Manufacture Date:</span>
                <span>{lotData.manufacturing_date}</span>
              </div>
              <div className="lot-field">
                <span>Lot Number:</span>
                <span>{lotData.lot_number}</span>
              </div>
              <div className="lot-field">
                <span>Part Name:</span>
                <span>{lotData.part_name}</span>
              </div>
              <div className="lot-field">
                <span>Part Type:</span>
                <span>{lotData.part_type}</span>
              </div>
              <div className="lot-field">
                <span>Part Reference Number:</span>
                <span>{lotData.part_reference_number}</span>
              </div>
            </div>
  
            {/* Personnel Information */}
            <div className="lot-section">
              <h2>Machine Users</h2>
              <div className="lot-field">
                <span>Machine Operator:</span>
                <span>{lotData.machine_operator}</span>
              </div>
              <div className="lot-field">
                <span>Machine Inspection:</span>
                <span>{lotData.machine_inspection}</span>
              </div>
            </div>
  
            {/* Weight Information */}
            <div className="lot-section">
              <h2>Weight Details</h2>
              <div className="lot-field">
                <span>Part Per Weight:</span>
                <span>{lotData.part_per_weight}</span>
              </div>
              <div className="lot-field">
                <span>Total Part Weight:</span>
                <span>{lotData.total_part_weight}</span>
              </div>
              <div className="lot-field">
                <span>Quantity:</span>
                <span>{lotData.quantity}</span>
              </div>
            </div>
          </div>
  
          {/* Right Column */}
          <div className="lot-column">
            {/* Machine Information */}
            <div className="lot-section">
              <h2>Machine Information</h2>
              <div className="lot-field">
                <span>Machine Number:</span>
                <span>{lotData.machine_number}</span>
              </div>
              <div className="lot-field">
                <span>Material Lot Number:</span>
                <span>{lotData.material_lot_number}</span>
              </div>
              <div className="lot-field">
                <span>Internal Number:</span>
                <span>{lotData.internal_lot_number}</span>
              </div>
            </div>
  
            {/* Dimensions */}
            <div className="lot-section">
              <h2>Dimensions (mm)</h2>
              <div className="lot-field">
                <span>Part Length (mm):</span>
                <span>{lotData.part_length_mm}</span>
              </div>
              <div className="lot-field">
                <span>Part Length including waste (1.4mm):</span>
                <span>{lotData.part_length_mm_include_waste}</span>
              </div>
              <div className="lot-field">
                <span>Manufacturing with wastage length (mm):</span>
                <span>{lotData.part_manufacturing_with_wastage_length_mm}</span>
              </div>
              <div className="lot-field">
                <span>Manufacturing with wastage length (No's):</span>
                <span>{lotData.part_manufacturing_with_wastage_length_nos}</span>
              </div>
            </div>
  
            {/* Bar Information */}
            <div className="lot-section">
              <h2>Bar Details</h2>
              <div className="lot-field">
                <span>Issue bar for manufacturing:</span>
                <span>{lotData.issue_bar_for_manufacturing}</span>
              </div>
              <div className="lot-field">
                <span>Return balance bars (No's):</span>
                <span>{lotData.return_balance_bar_in_nos}</span>
              </div>
              <div className="lot-field">
                <span>Return balance bar (mm):</span>
                <span>{lotData.return_balance_bar_in_mm}</span>
              </div>
              <div className="lot-field">
                <span>Per length mm wastage (150mm):</span>
                <span>{lotData.per_length_mm_wastage_150mm}</span>
              </div>
              <div className="lot-field">
                <span> wastage (150mm):</span>
                <span>{lotData.wastage_150mm}</span>
              </div>
              <div className="lot-field">
                <span>Final used bar (No's):</span>
                <span>{lotData.final_used_bar_in_no}</span>
              </div>
              <div className="lot-field">
                <span>Final used bar (MM):</span>
                <span>{lotData.final_used_bar_in_mm}</span>
              </div>
              <div className="lot-field">
                <span>Each part waste:</span>
                <span>{lotData.each_part_waste}</span>
              </div>
            </div>
          </div>
        </div>
          
          <Tabs />
      </div>
    );
  };
  
  export default LotDetails;
